import { useState, useRef, useEffect } from 'react';
import { fetchRFPDocuments, fetchMyDocuments } from '../../../services/rfpService';
import api from '../../../../../services/api';

/**
 * Custom hook encapsulating all document management logic for DocumentInfoModal.
 * Zero UI — only state, handlers, and API calls.
 */
const useDocumentInfo = ({
    isOpen,
    rfpId,
    isManager,
    setDocuments,
    setDocumentAssignments,
    documents,
    documentAssignments,
    teamMembers,
    onClose,
    fetchAssignedUsersData
}) => {
    const [showDropdownForDoc, setShowDropdownForDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [alertData, setAlertData] = useState({ show: false, message: '', type: 'notice' });
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, userName: '', fileName: '', docId: '', userId: '' });
    const [fileDeleteConfirm, setFileDeleteConfirm] = useState({ show: false, fileName: '', docId: '' });
    const [uploadConfirm, setUploadConfirm] = useState({ show: false, file: null, reanalyze: false });

    const fileInputRef = useRef(null);
    const selectRef = useRef(null);

    const membersList = teamMembers || [];

    useEffect(() => {
        if (showDropdownForDoc && selectRef.current) {
            try {
                if (selectRef.current.showPicker) selectRef.current.showPicker();
            } catch (error) {
                console.warn('showPicker failed or not supported:', error);
            }
        }
    }, [showDropdownForDoc]);

    const loadDocs = async () => {
        try {
            setIsLoading(true);
            let response;
            if (isManager) {
                if (!rfpId) return;
                response = await fetchRFPDocuments(rfpId);
            } else {
                response = await fetchMyDocuments();
            }
            const docsList = response.data?.documents || response.data || [];
            const initialAssignments = {};
            if (Array.isArray(docsList)) {
                docsList.forEach(doc => {
                    const dId = doc.doc_id || doc._id || doc.id;
                    if (dId && Array.isArray(doc.allowed_users)) {
                        initialAssignments[dId] = doc.allowed_users.map(u =>
                            typeof u === 'object' ? String(u.user_id || u._id || u.id) : String(u)
                        );
                    }
                });
            }
            if (Object.keys(initialAssignments).length > 0) {
                setDocumentAssignments(prev => ({ ...prev, ...initialAssignments }));
            }
            const formattedDocs = (Array.isArray(docsList) ? docsList : []).map(doc => ({
                id: doc.doc_id || doc._id || doc.id || doc.url || Math.random(),
                doc_id: doc.doc_id,
                name: doc.filename || doc.original_name || doc.name || 'Untitled Document',
                url: doc.url,
                doc_type: doc.doc_type,
                size: doc.file_size
                    ? (doc.file_size / 1024 / 1024).toFixed(1) + ' MB'
                    : (doc.size || '0.3 MB'),
                uploaded_by: doc.uploaded_by,
                allowed_users: doc.allowed_users || []
            }));
            setDocuments(formattedDocs);
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadDocs();
        }
    }, [isOpen, rfpId, isManager]);

    const handleAddMember = (docId, memberId) => {
        if (!memberId) return;
        if (memberId === 'all') {
            const allIds = membersList.map(m => String(m.user_id || m._id || m.id));
            setDocumentAssignments(prev => {
                const current = prev[docId] || [];
                const combined = [...new Set([...current, ...allIds])];
                return { ...prev, [docId]: combined };
            });
            setShowDropdownForDoc(null);
            return;
        }
        const member = membersList.find(m => String(m.user_id || m._id || m.id || m.name) === memberId);
        if (!member) return;
        const targetId = String(member.user_id || member._id || member.id);
        setDocumentAssignments(prev => {
            const current = prev[docId] || [];
            if (current.includes(targetId)) return prev;
            return { ...prev, [docId]: [...current, targetId] };
        });
        setShowDropdownForDoc(null);
    };

    const handleRemoveMember = (docId, targetId) => {
        const member = membersList.find(m => String(m.user_id || m._id || m.id) === targetId);
        const doc = documents.find(d => (d.doc_id || d.id) === docId);
        const userName = member ? (member.name || `${member.first_name} ${member.last_name}`) : 'Unknown User';
        const fileName = doc ? (doc.filename || doc.name) : 'this file';
        setDeleteConfirm({ show: true, userName, fileName, docId, userId: targetId });
    };

    const confirmRemoveAccess = async () => {
        const { docId, userId } = deleteConfirm;
        try {
            setIsLoading(true);
            setDeleteConfirm(prev => ({ ...prev, show: false }));
            const payload = { user_id: userId };
            await api.delete(`/extraction/v1/${rfpId}/documents/${docId}/access`, { data: payload });
            setDocumentAssignments(prev => {
                const current = prev[docId] || [];
                return { ...prev, [docId]: current.filter(id => id !== userId) };
            });
            await loadDocs();
            setAlertData({ show: true, message: `Access removed for ${deleteConfirm.userName} from ${deleteConfirm.fileName}`, type: 'notice' });
        } catch (error) {
            console.error('Error removing access:', error);
            setAlertData({ show: true, message: 'Failed to remove access. Please try again.', type: 'notice' });
        } finally {
            setIsLoading(false);
        }
    };

    const initiateDeleteFile = (doc) => {
        setFileDeleteConfirm({ show: true, fileName: doc.filename || doc.name, docId: doc.doc_id || doc.id });
    };

    const confirmDeleteFile = async () => {
        const { docId, fileName } = fileDeleteConfirm;
        try {
            setIsLoading(true);
            setFileDeleteConfirm(prev => ({ ...prev, show: false }));
            await api.delete(`/storage/v1/${rfpId}/documents/${docId}`);
            setDocuments(prev => prev.filter(d => (d.doc_id || d.id) !== docId));
            setDocumentAssignments(prev => {
                const updated = { ...prev };
                delete updated[docId];
                return updated;
            });
            setAlertData({ show: true, message: `Document "${fileName}" has been deleted successfully.`, type: 'notice' });
        } catch (error) {
            console.error('Error deleting document:', error);
            setAlertData({ show: true, message: 'Failed to delete the document. Please try again.', type: 'notice' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirm = async () => {
        try {
            setIsLoading(true);
            const payload = {
                assignments: Object.entries(documentAssignments)
                    .filter(([, userIds]) => userIds && userIds.length > 0)
                    .map(([docId, userIds]) => ({ doc_id: docId, user_ids: userIds }))
            };
            const response = await api.post(`/extraction/v1/${rfpId}/documents/matrix-access`, payload);
            await loadDocs();
            setAlertData({ show: true, message: response.data.message || 'Access updated successfully!', onOk: onClose });
        } catch (error) {
            console.error('Error updating access:', error);
            setAlertData({ show: true, message: 'Failed to update access. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) setUploadConfirm({ show: true, file, reanalyze: false });
        e.target.value = '';
    };

    const confirmUpload = async () => {
        const { file, reanalyze } = uploadConfirm;
        if (!file) return;
        try {
            setIsLoading(true);
            setUploadConfirm({ show: false, file: null, reanalyze: false });
            const formData = new FormData();
            formData.append('file', file);
            formData.append('doc_type', 'supporting_doc');
            formData.append('reanalyze', String(reanalyze));
            await api.post(`/extraction/v1/${rfpId}/upload-enrichment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (isManager) {
                const refreshResponse = await fetchRFPDocuments(rfpId);
                const docsList = refreshResponse.data?.documents || refreshResponse.data || [];
                const formattedDocs = (Array.isArray(docsList) ? docsList : []).map(doc => ({
                    id: doc.doc_id || doc._id || doc.id || doc.url || Math.random(),
                    name: doc.filename || doc.original_name || doc.name || 'Untitled Document',
                    url: doc.url,
                    doc_type: doc.doc_type,
                    size: doc.file_size ? (doc.file_size / 1024 / 1024).toFixed(1) + ' MB' : (doc.size || '0.3 MB'),
                    uploaded_by: doc.uploaded_by
                }));
                setDocuments(formattedDocs);
            }
            setAlertData({ show: true, message: `File "${file.name}" uploaded successfully.`, type: 'notice' });
        } catch (error) {
            console.error('Error uploading document:', error);
            setAlertData({ show: true, message: 'Failed to upload the document. Please try again.', type: 'notice' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowDropdown = async (documentId) => {
        if (typeof fetchAssignedUsersData === 'function') {
            const data = await fetchAssignedUsersData();
            if (data && data.is_contributors_present === false) {
                setAlertData({ show: true, message: data.message || 'Please assign users first to grant them document view access.' });
                return;
            }
        }
        setShowDropdownForDoc(documentId);
    };

    return {
        showDropdownForDoc, setShowDropdownForDoc,
        isLoading, alertData, setAlertData,
        deleteConfirm, setDeleteConfirm,
        fileDeleteConfirm, setFileDeleteConfirm,
        uploadConfirm, setUploadConfirm,
        fileInputRef, selectRef, membersList,
        handleAddMember, handleRemoveMember,
        confirmRemoveAccess, initiateDeleteFile,
        confirmDeleteFile, handleConfirm,
        handleUploadClick, handleFileChange,
        confirmUpload, handleShowDropdown
    };
};

export default useDocumentInfo;
